'use client'

import { Button } from '@/components/ui/button'
import { ChevronDown, ChevronUp } from 'lucide-react'
import Link from 'next/link'
import { useState } from 'react'

type NavItem = {
  title: string
  description: string
  bullets?: string[]
  cta?: { label: string; href: string }
}

const NAV_ITEMS: NavItem[] = [
  {
    title: 'Running',
    description:
      'Lightweight, breathable, sweat-managing kits for first-timers, weekend joggers, and serious distance runners. Designed and tested with our Coolmate Running Club community — the people who use them in tropical heat, weekly, on real roads.',
    bullets: [
      'Performance tees and singlets in moisture-wicking knits',
      'Running shorts (lined and unlined) engineered for chafe-free long distance',
      'Compression layers, caps, socks, accessories',
    ],
    cta: { label: 'Shop running', href: '/collection/running' },
  },
  {
    title: 'Pickleball',
    description:
      'Built for the fastest-growing sport in Southeast Asia. Our pickleball line is designed with court-side input from partner clubs and players who pushed us on grip, ventilation, and movement at the net.',
    bullets: [
      'Court-ready polos, tees, and skorts',
      'Athletic shorts with paddle and ball pockets',
      'Branded collaborations with leading Malaysian pickleball clubs',
    ],
    cta: { label: 'Shop Pickleball', href: '/collection/pickleball' },
  },
  {
    title: 'Everyday Performance',
    description:
      'The pieces you reach for when you are not training but still want clothes that move with you, soft-touch tees, joggers, lounge layers. Same fabric philosophy as our activewear, designed for the long hours in between.',
  },
  {
    title: 'Daily Essentials',
    description:
      'The high-quality basics that quietly do their job. Underwear, socks, undershirts — engineered to the same fabric and durability standards as the rest of the kit, because the layer closest to your skin matters most.',
    cta: { label: 'Shop Underwear', href: '/collection/mens-underwear' },
  },
]

export const AccordionNav = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div className='flex flex-col gap-2'>
      {NAV_ITEMS.map((item, index) => {
        const isOpen = openIndex === index
        return (
          <button
            key={item.title}
            onClick={() => setOpenIndex(isOpen ? null : index)}
            className='flex w-full items-start gap-2.5 rounded-xl bg-white p-5 text-left transition-shadow hover:shadow-sm'
          >
            <div className='flex flex-1 flex-col gap-2.5'>
              <div className='flex items-center gap-2'>
                <span className='flex-1 font-criteria text-lg font-medium text-neutral-900'>
                  {item.title}
                </span>
              </div>

              {isOpen && (
                <div className='flex flex-col gap-8'>
                  <div className='flex flex-col gap-2'>
                    <p className='text-sm leading-6 text-neutral-700 md:text-base'>
                      {item.description}
                    </p>
                    {item.bullets && (
                      <ul className='list-disc pl-5 text-sm italic leading-6 text-neutral-700 md:text-base'>
                        {item.bullets.map((bullet) => (
                          <li key={bullet}>{bullet}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {item.cta && (
                    <Button
                      variant='black'
                      size='small'
                      className='w-fit rounded-full uppercase'
                      asChild
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Link href={item.cta.href}>{item.cta.label}</Link>
                    </Button>
                  )}
                </div>
              )}
            </div>

            <span className='mt-0.5 shrink-0 text-neutral-900'>
              {isOpen ? (
                <ChevronUp className='h-6 w-6' />
              ) : (
                <ChevronDown className='h-6 w-6' />
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}
